import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { SearchBox } from 'tea-component';
import { get } from 'lodash-es';
import CommonTable from '@/pages/dashboard/components/CommonTable';
import type { IColumn, ICommonTableProps, ICommonTableRef } from '@/pages/dashboard/components/CommonTable/type';
import { expandColumnsForDownload } from '@/pages/dashboard/components/CommonTable/utils';
import { useDownloadDataSource } from '@/pages/dashboard/components/CommonTable/hooks/useDownloadDataSource';
import Render from '@/pages/dashboard/components/PageContainer/Render';
import { useLocal, useSelectorMap } from '@/pages/dashboard/utils/customHook';
import { getName } from '@home/utils/common';
import { PR_WORKSPACE_TABS_TOOLBAR_HOST_ID } from '@home/pages/studio/pr/prHubSubTabs';
import { partitionPrWorkspaceTableOperations } from '@home/pages/studio/pr/prTableOperationUtils';
import classNames from 'classnames';
import '@home/pages/studio/pr/components/PrMediaTable/index.scss';

export type PrPressCommonTableProps = ICommonTableProps & {
  showSearch?: boolean;
};

/**
 * PR 工作台中 Regions / Articles / … 等：与 PrMediaTable 一致，将搜索 + 下载通过 Portal 放到 Tab 行右侧；extraParams.keyword 与列表接口对齐。
 */
const PrPressCommonTable = forwardRef<ICommonTableRef, PrPressCommonTableProps>((props, ref) => {
  const {
    showSearch = true,
    data,
    dataKey = 'tableData',
    dataPath = 'metrics_list',
    totalRecordPath = 'row_count',
    changeData,
    commonBaseTableProps: commonBaseIn,
    metricsCardWrapProps: metricsCardIn,
    ...rest
  } = props;

  const {
    titleEn = '',
    titleCn = '',
    operation: metricsOperation,
    contentHeader: pageContentHeader,
    ...restMetrics
  } = metricsCardIn || {};
  const { legendSlot, otherSlot } = partitionPrWorkspaceTableOperations(metricsOperation);

  const { columns: columnsFromConfig = [], style: userTableStyle, ...restCommonBase } = commonBaseIn || {};
  const columns = (columnsFromConfig || []) as IColumn[];

  const [toolbarHost, setToolbarHost] = useState<HTMLElement | null>(null);
  const { getFilterItemText } = useSelectorMap();

  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    setToolbarHost(document.getElementById(PR_WORKSPACE_TABS_TOOLBAR_HOST_ID));
  }, []);

  const { isEN } = useLocal();
  const apiBlock = (data as Record<string, unknown> | undefined)?.[String(dataKey)];
  const apiData = (apiBlock as { data?: Record<string, unknown> } | undefined)?.data;
  const apiLoading = Boolean((apiBlock as { loading?: boolean } | undefined)?.loading);
  const totalRec = Number(get(apiData, totalRecordPath)) || 0;
  const inTabToolbar = Boolean(toolbarHost);
  const listPath = dataPath || 'metrics_list';
  const rawRows = get(apiData, listPath);
  const rows = (Array.isArray(rawRows) ? rawRows : []) as Record<string, unknown>[];

  const [keywordInput, setKeywordInput] = useState('');
  useEffect(() => {
    const kw = (data as { extraParams?: { keyword?: string } } | undefined)?.extraParams?.keyword;
    setKeywordInput(typeof kw === 'string' ? kw : '');
  }, [data]);

  const pushKeyword = useCallback(
    (raw: string) => {
      if (!changeData) return;
      const next = (raw || '').trim();
      const extraParams = {
        ...(data as { extraParams?: Record<string, unknown> } | undefined)?.extraParams,
        keyword: next,
        page: 1,
      };
      changeData('extraParams', extraParams);
    },
    [changeData, data],
  );

  const searchNode =
    showSearch && changeData ? (
      <div
        className={classNames('dbh-pr-media-table__search', {
          'dbh-pr-media-table__search--in-tab-toolbar': inTabToolbar,
        })}
      >
        <SearchBox
          className="dbh-pr-media-table__search-input"
          placeholder={isEN ? 'Search' : '搜索'}
          value={keywordInput}
          onChange={(v) => {
            setKeywordInput(v);
          }}
          onSearch={(v) => {
            pushKeyword(v ?? '');
          }}
          onClear={() => {
            setKeywordInput('');
            pushKeyword('');
          }}
        />
      </div>
    ) : null;

  const downloadColumns = useMemo(
    () => expandColumnsForDownload(columns, isEN),
    [columns, isEN],
  );
  const downloadDataSource = useDownloadDataSource(rows, columns, getFilterItemText);

  const showOpsInTabToolbar = inTabToolbar && otherSlot.length > 0;

  const tabToolbarPortal: ReactNode =
    inTabToolbar && toolbarHost
      ? (createPortal(
        <div className="dbh-pr-workspace-tabs__toolbar-inner">
          {searchNode}
          {showOpsInTabToolbar
            ? otherSlot.map((item, index) => {
              const opName = (item as { name?: string }).name;
              return (
                <Render
                  key={`pr-press-header-op-${opName ?? 'op'}-${index}`}
                  config={item as { name: string; type: string; [k: string]: unknown }}
                  tableColumns={downloadColumns}
                  tableTitle={getName(isEN, titleEn, titleCn)}
                  tableData={downloadDataSource}
                  data={data}
                  changeData={changeData}
                  {...(opName === 'DownloadButton' || opName === 'PrDownloadButton'
                    ? { disabled: apiLoading || totalRec <= 0 }
                    : {})}
                  {...rest}
                />
              );
            })
            : null}
        </div>,
        toolbarHost,
      ) as ReactNode)
      : null;

  const contentHeaderInCard = (
    <>
      {!inTabToolbar ? searchNode : null}
      {pageContentHeader as React.ReactNode}
    </>
  );

  return (
    <div className="dbh-pr-media-table__root">
      {tabToolbarPortal}
      <CommonTable
        ref={ref}
        data={data}
        dataKey={dataKey}
        dataPath={dataPath}
        totalRecordPath={totalRecordPath}
        changeData={changeData}
        commonBaseTableProps={{
          ...restCommonBase,
          style: {
            overflowX: 'auto',
            ...(userTableStyle || {}),
            maxHeight: 'none',
            minHeight: 'unset',
          },
          columns: columnsFromConfig,
        }}
        metricsCardWrapProps={{
          ...restMetrics,
          titleEn,
          titleCn,
          contentHeader: contentHeaderInCard,
          operation: inTabToolbar ? (legendSlot.length ? (legendSlot as never) : undefined) : metricsOperation,
        }}
        {...rest}
      />
    </div>
  );
});

PrPressCommonTable.displayName = 'PrPressCommonTable';

export default PrPressCommonTable;
